/**
 * Accessible loading spinner. Colour is inherited via `currentColor`, so set a
 * text colour on the parent (or via `className`) to tint it.
 */
import type { ReactElement } from 'react';

export interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Screen-reader label; visually hidden. */
  label?: string;
}

const sizeMap: Record<NonNullable<SpinnerProps['size']>, string> = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-10 w-10',
};

/** Animated SVG spinner with an sr-only status label. */
export default function Spinner({
  size = 'md',
  className = '',
  label = 'Đang tải',
}: SpinnerProps): ReactElement {
  return (
    <span role="status" aria-live="polite" className={`inline-flex ${className}`.trim()}>
      <svg
        className={`animate-spin ${sizeMap[size]}`}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>
      <span className="sr-only">{label}</span>
    </span>
  );
}
